/**
 * IXVerify v2 — History (ixv2 WP-3.3; plan §4 "History (+ filters,
 * presigned PDF)").
 *
 * Paginated session history (v1 was unbounded) with status filter + "mine"
 * chip. Published sessions offer:
 *   - "Report" — opens `ReportPreview`, which renders the sealed PDF in place
 *     (the presign is still ON DEMAND, `GET /sessions/:id/report-url`, and the
 *     URL is never stored — v1's expiring-stored-URL defect, fixed);
 *     downloading is a button inside the preview, not the only way to read it;
 *   - "Resend" — the redistribution modal: saved recipients (checkboxes) +
 *     free-typed addresses, all validated server-side against the R3 rule
 *     (site users / CRIO intake / admin allow-list); a rejected address fails
 *     the whole request and is named in the error. With nothing selected the
 *     modal retries the report's failed deliveries.
 *
 * Data is the PHI-free summary projection — no DOB on this surface (R3).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { History as HistoryIcon, FileText, Send, Inbox, UserCheck } from 'lucide-react';
import {
  PageHeader,
  RefreshButton,
  Modal,
  EmptyState,
  FilterChip,
  useToast,
  primaryBtnCls,
  secondaryBtnCls,
  fieldCls,
} from '../../ui';
import { useAuth } from '../../contexts/AuthContext';
import {
  listSessions,
  listStudies,
  listRecipients,
  resendReport,
  errorMessage,
  isRecipientNotAllowed,
  rejectedRecipients,
  IxvSessionSummary,
  IxvSavedRecipient,
  IxvStudy,
  SESSION_STATUSES,
} from '../lib/api';
import { SessionStatusPill } from '../components/shared/badges';
import SecondCheckSheet from '../components/wizard/SecondCheckSheet';
import ReportPreview from '../components/shared/ReportPreview';

const PAGE_SIZE = 25;
const REPORTABLE = new Set(['completed', 'second_check_pending', 'verified']);
// WP-4.1: a published-but-unverified session can (re-)request a second check;
// a pending one links to the review page (/ixverify/checks/:sessionId — the
// server resolves session ids because checks are UNIQUE per session).
const CAN_REQUEST_CHECK = new Set(['completed']);
const HAS_CHECK = new Set(['second_check_pending', 'verified']);

const STATUS_LABELS: Record<string, string> = {
  '': 'All statuses',
  draft: 'Draft',
  staged: 'Ready to dispense',
  capturing: 'Capturing',
  review: 'In review',
  completed: 'Published',
  second_check_pending: 'Second check pending',
  verified: 'Verified',
  mismatch_recorded: 'Mismatch recorded',
  cancelled: 'Cancelled',
};

function fmtDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

export default function History() {
  const { toast } = useToast();
  useAuth(); // shell guarantees a session; hook kept for parity with sibling pages

  // Notification deep links land here as /ixverify/history?session=<id>
  // (ixv2 WP-3.4) — the named session's row is highlighted.
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('session');

  const [sessions, setSessions] = useState<IxvSessionSummary[]>([]);
  const [studies, setStudies] = useState<IxvStudy[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [mineOnly, setMineOnly] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // Report preview target (the presign now happens inside ReportPreview).
  const [reportTarget, setReportTarget] = useState<IxvSessionSummary | null>(null);

  // Second-check request sheet (WP-4.1 — the re-request path after a
  // rejection/expiry nudge; the wizard's success screen covers fresh publishes).
  const [checkTarget, setCheckTarget] = useState<IxvSessionSummary | null>(null);

  // Resend modal state.
  const [resendTarget, setResendTarget] = useState<IxvSessionSummary | null>(null);
  const [savedRecipients, setSavedRecipients] = useState<IxvSavedRecipient[]>([]);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [extraEmails, setExtraEmails] = useState('');
  const [resendError, setResendError] = useState<string | null>(null);
  const [resendBusy, setResendBusy] = useState(false);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listSessions({
        status: status || undefined,
        mine: mineOnly,
        limit: PAGE_SIZE,
        offset,
      });
      setSessions(res.sessions);
      setHasMore(res.sessions.length === PAGE_SIZE);
    } catch (err) {
      toast(errorMessage(err, 'Failed to load history'), { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [status, mineOnly, offset, toast]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  useEffect(() => {
    listStudies().then(setStudies).catch(() => setStudies([]));
  }, []);

  const protocolById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of studies) map.set(s.id, s.protocol_id);
    return map;
  }, [studies]);

  const openResend = async (session: IxvSessionSummary) => {
    setResendTarget(session);
    setChecked(new Set());
    setExtraEmails('');
    setResendError(null);
    try {
      setSavedRecipients(await listRecipients());
    } catch {
      setSavedRecipients([]);
    }
  };

  const submitResend = async () => {
    if (!resendTarget) return;
    const typed = extraEmails
      .split(/[\s,;]+/)
      .map((e) => e.trim())
      .filter(Boolean);
    const recipients = [...checked, ...typed];
    setResendBusy(true);
    setResendError(null);
    try {
      const result = await resendReport(resendTarget.id, recipients);
      toast(
        recipients.length > 0
          ? `Report queued to ${result.queued.length} recipient${result.queued.length === 1 ? '' : 's'}`
          : 'Failed deliveries queued for retry',
      );
      setResendTarget(null);
    } catch (err) {
      if (isRecipientNotAllowed(err)) {
        const rejected = rejectedRecipients(err).map((r) => r.to).join(', ');
        setResendError(
          rejected
            ? `Not permitted for this site: ${rejected}. Recipients must be site users, the CRIO intake address, or on the admin allow-list.`
            : errorMessage(err, 'Recipient not allowed'),
        );
      } else {
        setResendError(errorMessage(err, 'Failed to queue the resend'));
      }
    } finally {
      setResendBusy(false);
    }
  };

  const toggleChecked = (email: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const changeFilter = (next: string) => {
    setStatus(next);
    setOffset(0);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        icon={HistoryIcon}
        title="History"
        actions={<RefreshButton onClick={fetchSessions} loading={loading} />}
      />

      {/* Chromeless filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <label className="sr-only" htmlFor="ixv-history-status">Filter by status</label>
        <select
          id="ixv-history-status"
          className={`${fieldCls} min-h-[44px] sm:min-h-0`}
          value={status}
          onChange={(e) => changeFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          {SESSION_STATUSES.map((s) => (
            <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>
          ))}
        </select>
        <FilterChip
          active={mineOnly}
          onClick={() => { setMineOnly((v) => !v); setOffset(0); }}
        >
          My sessions
        </FilterChip>
      </div>

      {!loading && sessions.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title="No sessions found"
          hint={status ? `No ${STATUS_LABELS[status]?.toLowerCase() || status} sessions on this page.` : 'Dispensing sessions appear here as they are created.'}
          variant="card"
        />
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 sm:block">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800/50">
                  <tr>
                    {['Verification code', 'Status', 'Study', 'Visit', 'Created', 'Completed', 'Actions'].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {sessions.map((s) => (
                    <tr
                      key={s.id}
                      data-highlighted={s.id === highlightId || undefined}
                      className={`transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                        s.id === highlightId ? 'bg-primary-50/60 dark:bg-primary-900/10' : ''
                      }`}
                    >
                      <td className="px-4 py-3 text-sm font-medium tabular-nums text-gray-900 dark:text-gray-100">{s.verification_code || '—'}</td>
                      <td className="px-4 py-3"><SessionStatusPill status={s.status} /></td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{(s.site_study_id && protocolById.get(s.site_study_id)) || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{s.visit_name || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{fmtDateTime(s.created_at)}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{fmtDateTime(s.completed_at)}</td>
                      <td className="px-4 py-3">
                        {REPORTABLE.has(s.status) && (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setReportTarget(s)}
                              className="inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-800 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 dark:text-primary-400 dark:hover:text-primary-300"
                            >
                              <FileText className="h-4 w-4" aria-hidden="true" />
                              Report
                            </button>
                            <button
                              type="button"
                              onClick={() => openResend(s)}
                              className="inline-flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:text-gray-200"
                            >
                              <Send className="h-4 w-4" aria-hidden="true" />
                              Resend
                            </button>
                            {CAN_REQUEST_CHECK.has(s.status) && (
                              <button
                                type="button"
                                onClick={() => setCheckTarget(s)}
                                className="inline-flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:text-gray-200"
                              >
                                <UserCheck className="h-4 w-4" aria-hidden="true" />
                                Second check
                              </button>
                            )}
                            {HAS_CHECK.has(s.status) && (
                              <Link
                                to={`/ixverify/checks/${s.id}`}
                                className="inline-flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:text-gray-200"
                              >
                                <UserCheck className="h-4 w-4" aria-hidden="true" />
                                Check
                              </Link>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile cards */}
          <ul className="space-y-2 sm:hidden">
            {sessions.map((s) => (
              <li
                key={s.id}
                data-highlighted={s.id === highlightId || undefined}
                className={`rounded-xl border bg-white p-4 dark:bg-gray-800 ${
                  s.id === highlightId
                    ? 'border-primary-400 bg-primary-50/60 dark:border-primary-700 dark:bg-primary-900/10'
                    : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-medium tabular-nums text-gray-900 dark:text-gray-100">{s.verification_code || '—'}</span>
                  <SessionStatusPill status={s.status} />
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {[(s.site_study_id && protocolById.get(s.site_study_id)) || null, s.visit_name, fmtDateTime(s.created_at)].filter(Boolean).join(' · ')}
                </p>
                {REPORTABLE.has(s.status) && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setReportTarget(s)}
                      className={`${secondaryBtnCls} flex-1 justify-center`}
                    >
                      <FileText className="h-4 w-4" aria-hidden="true" />
                      Report
                    </button>
                    <button type="button" onClick={() => openResend(s)} className={`${secondaryBtnCls} flex-1 justify-center`}>
                      <Send className="h-4 w-4" aria-hidden="true" />
                      Resend
                    </button>
                    {CAN_REQUEST_CHECK.has(s.status) && (
                      <button type="button" onClick={() => setCheckTarget(s)} className={`${secondaryBtnCls} flex-1 justify-center`}>
                        <UserCheck className="h-4 w-4" aria-hidden="true" />
                        Second check
                      </button>
                    )}
                    {HAS_CHECK.has(s.status) && (
                      <Link to={`/ixverify/checks/${s.id}`} className={`${secondaryBtnCls} flex-1 justify-center`}>
                        <UserCheck className="h-4 w-4" aria-hidden="true" />
                        Check
                      </Link>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>

          {/* Pager */}
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              className={secondaryBtnCls}
              disabled={offset === 0 || loading}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Previous
            </button>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {offset + 1}–{offset + sessions.length}
            </span>
            <button
              type="button"
              className={secondaryBtnCls}
              disabled={!hasMore || loading}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next
            </button>
          </div>
        </>
      )}

      {/* Report preview — read it here; download is a button inside it */}
      {reportTarget && (
        <ReportPreview
          sessionId={reportTarget.id}
          verificationCode={reportTarget.verification_code}
          onClose={() => setReportTarget(null)}
        />
      )}

      {/* Second-check request sheet (WP-4.1) */}
      {checkTarget && (
        <SecondCheckSheet
          sessionId={checkTarget.id}
          onClose={() => setCheckTarget(null)}
          onRequested={() => {
            setCheckTarget(null);
            toast('Second check requested — the verifier has been notified');
            fetchSessions();
          }}
        />
      )}

      {/* Resend modal */}
      <Modal
        isOpen={resendTarget !== null}
        onClose={() => setResendTarget(null)}
        title={`Resend report ${resendTarget?.verification_code || ''}`}
      >
        <div className="space-y-4 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Pick saved recipients and/or type addresses. Recipients must be site users, the
            CRIO intake address, or on the admin allow-list — the report contains PHI.
            Leave everything empty to retry failed deliveries.
          </p>
          {savedRecipients.length > 0 && (
            <fieldset>
              <legend className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Saved recipients</legend>
              <ul className="space-y-1">
                {savedRecipients.map((r) => (
                  <li key={r.id}>
                    <label className="flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg px-2 text-sm text-gray-900 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700 sm:min-h-0 sm:py-1.5">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
                        checked={checked.has(r.email)}
                        onChange={() => toggleChecked(r.email)}
                      />
                      <span>{r.email}</span>
                      {r.label && <span className="text-xs text-gray-500 dark:text-gray-400">({r.label})</span>}
                    </label>
                  </li>
                ))}
              </ul>
            </fieldset>
          )}
          <div>
            <label htmlFor="ixv-resend-extra" className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Other addresses (comma-separated)
            </label>
            <input
              id="ixv-resend-extra"
              type="text"
              className={`${fieldCls} w-full`}
              placeholder="coordinator@yoursite.com"
              value={extraEmails}
              onChange={(e) => setExtraEmails(e.target.value)}
            />
          </div>
          {resendError && (
            <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400" role="alert">
              {resendError}
            </p>
          )}
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-gray-100 p-4 dark:border-gray-700">
          <button type="button" className={secondaryBtnCls} onClick={() => setResendTarget(null)}>Cancel</button>
          <button type="button" className={primaryBtnCls} onClick={submitResend} disabled={resendBusy}>
            <Send className="h-4 w-4" aria-hidden="true" />
            {checked.size > 0 || extraEmails.trim() ? 'Queue send' : 'Retry failed deliveries'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
